// Connection state machine — xstate v5

import { setup, assign } from 'xstate';

export type ConnectionContext = Readonly<{
  serverUrl: string;
  retryCount: number;
  lastEventId?: number;
  errorMessage?: string;
}>;

type ConnectionEvents =
  | { type: 'CONNECT' }
  | { type: 'OPEN' }
  | { type: 'DISCONNECT' }
  | { type: 'RETRY' }
  | { type: 'MESSAGE'; id: number }
  | { type: 'ERROR'; message: string }
  | { type: 'RESET' };

export const connectionMachine = setup({
  types: {
    context: {} as ConnectionContext,
    events: {} as ConnectionEvents,
  },
  guards: {
    canRetry: ({ context }) => context.retryCount < 5,
  },
}).createMachine({
  id: 'connection',
  initial: 'disconnected',
  context: {
    serverUrl: '',
    retryCount: 0,
    lastEventId: undefined,
    errorMessage: undefined,
  },
  states: {
    disconnected: {
      on: {
        CONNECT: {
          target: 'connecting',
          actions: assign({
            retryCount: () => 0,
            errorMessage: () => undefined,
          }),
        },
      },
    },
    connecting: {
      on: {
        OPEN: {
          target: 'connected',
          actions: assign({
            retryCount: () => 0,
            errorMessage: () => undefined,
          }),
        },
        ERROR: {
          target: 'error',
          actions: assign({
            errorMessage: ({ event }) => event.message,
          }),
        },
      },
    },
    connected: {
      on: {
        DISCONNECT: { target: 'reconnecting' },
        MESSAGE: {
          actions: assign({
            lastEventId: ({ event }) => event.id,
          }),
        },
      },
    },
    reconnecting: {
      on: {
        RETRY: [
          {
            target: 'connecting',
            guard: 'canRetry',
            actions: assign({
              retryCount: ({ context }) => context.retryCount + 1,
            }),
          },
          {
            target: 'error',
            actions: assign({
              errorMessage: () => 'Max retries exceeded',
            }),
          },
        ],
      },
    },
    error: {
      on: {
        RESET: {
          target: 'disconnected',
          actions: assign({
            retryCount: () => 0,
            errorMessage: () => undefined,
          }),
        },
        CONNECT: {
          target: 'connecting',
          actions: assign({
            retryCount: () => 0,
            errorMessage: () => undefined,
          }),
        },
      },
    },
  },
});
